import { engineEintrag, getLang, useLang, woerterbuchEintrag, type Lang } from '@/i18n';

/**
 * Übersetzung von Meldungen, die *nicht* im App-Code stehen (docs/notes/engine-i18n.md):
 * Fehler und Warnungen aus `@airmail/engine`, Import-Hinweise aus `@airmail/io`
 * und Fehlertexte des Sync-Servers. Die Engine bleibt deutsch (ihre Tests prüfen
 * die deutschen Texte) — übersetzt wird erst beim Rendern.
 *
 * Reihenfolge: exakter Treffer im Wörterbuch (`dict/engine.ts`) → Muster für
 * Meldungen mit Zahlen/Namen (unten) → allgemeines Wörterbuch → deutscher Text.
 * Eine unbekannte Meldung erscheint also deutsch, nie leer.
 */

type M = RegExpMatchArray;
interface Pattern {
  re: RegExp;
  en: (m: M) => string;
  it: (m: M) => string;
}

/** Meldungen aus `validateResult` tragen optional den Satz-Vorspann „Satz 2: " */
const set = (body: string): RegExp => new RegExp(`^(?:Satz (\\d+): )?${body}$`);
const s = (m: M): string => (m[1] ? `Set ${m[1]}: ` : '');

/** Tiebreaker-Namen aus standings/standings.ts (LABELS) */
const CRIT: Record<string, [en: string, it: string]> = {
  Punkte: ['points', 'punti'],
  Punktdifferenz: ['point difference', 'differenza punti'],
  'erzielte Punkte': ['points scored', 'punti fatti'],
  Siege: ['wins', 'vittorie'],
};
const crit = (de: string | undefined, i: 0 | 1): string => CRIT[de ?? '']?.[i] ?? de ?? '';
const CRITS = Object.keys(CRIT).join('|');

/** Spielstatus aus reduce.ts (startMatch) */
const STATUS: Record<string, [en: string, it: string]> = {
  'noch ohne Teilnehmer': ['still without participants', 'ancora senza partecipanti'],
  bereit: ['ready', 'pronta'],
  'schon gestartet': ['already started', 'già iniziata'],
  'schon beendet': ['already finished', 'già terminata'],
  'als Walkover gewertet': ['counted as a walkover', 'contata come walkover'],
  abgesagt: ['cancelled', 'annullata'],
};
const status = (de: string | undefined, i: 0 | 1): string => STATUS[de ?? '']?.[i] ?? de ?? '';
/** verkettete Meldungen („a; b") einzeln übersetzen */
const each = (list: string | undefined): string => (list ?? '').split('; ').map(tm).join('; ');
/** „3× 4 Teams, 1× 3 Teams" */
const sizes = (p: string | undefined, word: string): string => (p ?? '').replace(/Teams/g, word);

const PATTERNS: Pattern[] = [
  // Engine — Fehler (engine/reduce.ts)
  { re: /^Bewerb (.+) nicht gefunden$/, en: (m) => `Competition ${m[1]} not found`, it: (m) => `Competizione ${m[1]} non trovata` },
  { re: /^Phase (.+) nicht gefunden$/, en: (m) => `Phase ${m[1]} not found`, it: (m) => `Fase ${m[1]} non trovata` },
  { re: /^Spiel (.+) nicht gefunden$/, en: (m) => `Match ${m[1]} not found`, it: (m) => `Partita ${m[1]} non trovata` },
  { re: /^Team (.+) nicht gefunden$/, en: (m) => `Team ${m[1]} not found`, it: (m) => `Squadra ${m[1]} non trovata` },
  {
    re: /^Gruppe (\S+): Gleichstand auf Platz (\d+) muss gelost werden$/,
    en: (m) => `Group ${m[1]}: the tie for place ${m[2]} must be decided by lot`,
    it: (m) => `Girone ${m[1]}: la parità al posto ${m[2]} va decisa per sorteggio`,
  },
  {
    re: /^Gruppe (\S+): Gleichstand auf Platz (\d+) — bitte losen \(group\.resolveLot\)$/,
    en: (m) => `Group ${m[1]}: tie for place ${m[2]} — please draw lots (group.resolveLot)`,
    it: (m) => `Girone ${m[1]}: parità al posto ${m[2]} — sorteggiare (group.resolveLot)`,
  },
  { re: /^(\d+) Spiel\(e\) laufen noch$/, en: (m) => `${m[1]} match(es) still running`, it: (m) => `${m[1]} partita/e ancora in corso` },
  { re: /^(\d+) Spiel\(e\) sind noch offen$/, en: (m) => `${m[1]} match(es) still open`, it: (m) => `${m[1]} partita/e ancora aperte` },
  { re: /^Spiel ist (.+) — nicht startbar$/, en: (m) => `Cannot start this match — it is ${status(m[1], 0)}`, it: (m) => `Impossibile avviare la partita — è ${status(m[1], 1)}` },
  { re: /^Feld (.+) ist inaktiv$/, en: (m) => `Court ${m[1]} is inactive`, it: (m) => `Il campo ${m[1]} non è attivo` },
  { re: /^Feld (.+) ist belegt$/, en: (m) => `Court ${m[1]} is busy`, it: (m) => `Il campo ${m[1]} è occupato` },
  {
    re: /^Spiel gehört auf Platz „(.+)" — (.+) liegt woanders$/,
    en: (m) => `Match belongs to venue “${m[1]}” — ${m[2]} is somewhere else`,
    it: (m) => `La partita appartiene alla sede «${m[1]}» — ${m[2]} si trova altrove`,
  },
  { re: /^(.+) ist zurückgezogen$/, en: (m) => `${m[1]} has withdrawn`, it: (m) => `${m[1]} si è ritirata` },
  { re: /^(.+) spielt gerade$/, en: (m) => `${m[1]} is playing right now`, it: (m) => `${m[1]} sta giocando in questo momento` },
  { re: /^Phasentyp (.+) ist noch nicht umgesetzt$/, en: (m) => `Phase type ${m[1]} is not implemented yet`, it: (m) => `Il tipo di fase ${m[1]} non è ancora implementato` },
  {
    re: /^Ergebnis unplausibel: (.+) \(mit force übersteuerbar\)$/,
    en: (m) => `Result implausible: ${each(m[1])} (can be overridden with force)`,
    it: (m) => `Risultato non plausibile: ${each(m[1])} (si può forzare)`,
  },
  // Engine — beschädigte Aktionen aus Datei oder Server (engine/form.ts, engine/reduce.ts)
  {
    re: /^Aktion (.+) beschädigt: Feld (.+) fehlt oder passt nicht$/,
    en: (m) => `Action ${m[1]} damaged: field ${m[2]} missing or invalid`,
    it: (m) => `Azione ${m[1]} danneggiata: campo ${m[2]} mancante o non valido`,
  },
  { re: /^Aktion nicht anwendbar: (.+)$/, en: (m) => `Action cannot be applied: ${tm(m[1] ?? '')}`, it: (m) => `Azione non applicabile: ${tm(m[1] ?? '')}` },
  // Engine — Warnungen (engine/reduce.ts)
  {
    re: /^Team hat (\d+) Spieler, Bewerb erwartet (\d+)$/,
    en: (m) => `Team has ${m[1]} players, the competition expects ${m[2]}`,
    it: (m) => `La squadra ha ${m[1]} giocatori, la competizione ne prevede ${m[2]}`,
  },
  { re: /^Teamname „(.+)" existiert schon$/, en: (m) => `Team name “${m[1]}” already exists`, it: (m) => `Il nome squadra «${m[1]}» esiste già` },
  {
    re: /^Nachgemeldet in Gruppe (.+): (\d+) zusätzliche Spiele$/,
    en: (m) => `Added late to group ${m[1]}: ${m[2]} additional matches`,
    it: (m) => `Iscritta in ritardo nel girone ${m[1]}: ${m[2]} partite in più`,
  },
  { re: /^(.+): ganze Zahl von 0 bis 64$/, en: (m) => `${tm(m[1] ?? '')}: whole number from 0 to 64`, it: (m) => `${tm(m[1] ?? '')}: numero intero da 0 a 64` },
  {
    re: /^(.+) ist wieder dabei: (\d+) Spiele? wieder offen, (\d+) Ergebnis(?:se zählen| zählt) wieder$/,
    en: (m) => `${m[1]} is back: ${m[2]} match(es) open again, ${m[3]} result(s) count again`,
    it: (m) => `${m[1]} è di nuovo in gara: ${m[2]} partite di nuovo aperte, ${m[3]} risultati contano di nuovo`,
  },
  {
    re: /^(\d+) Walkover im KO bleibt — das nächste Spiel läuft schon$/,
    en: (m) => `${m[1]} walkover in the knockout stays — the next match is already under way`,
    it: (m) => `${m[1]} walkover nell’eliminazione resta — la partita successiva è già in corso`,
  },
  { re: /^(.+) rückt von der Warteliste nach$/, en: (m) => `${m[1]} moves up from the waiting list`, it: (m) => `${m[1]} entra dalla lista d’attesa` },
  { re: /^(.+) steht auf der Warteliste$/, en: (m) => `${m[1]} is on the waiting list`, it: (m) => `${m[1]} è in lista d’attesa` },
  // Wechselnde Partner (schedule/partner.ts)
  {
    re: /^Gruppe (.+): Spielplan ohne (.+) neu erstellt$/,
    en: (m) => `Group ${m[1]}: schedule rebuilt without ${m[2]}`,
    it: (m) => `Girone ${m[1]}: calendario rifatto senza ${m[2]}`,
  },
  {
    re: /^(.+) abgemeldet: (\d+) offene Spiele in Gruppe (.+) entfallen, gespielte bleiben — gewertet wird pro Spiel$/,
    en: (m) => `${m[1]} withdrawn: ${m[2]} open matches in group ${m[3]} dropped, played ones stay — ranking is per match`,
    it: (m) => `${m[1]} ritirato: ${m[2]} partite aperte nel girone ${m[3]} saltano, quelle giocate restano — classifica per partita`,
  },
  {
    re: /^Gruppe (.+): Wechselnde Partner brauchen mindestens 4 Spieler — keine Spiele$/,
    en: (m) => `Group ${m[1]}: rotating partners need at least 4 players — no matches`,
    it: (m) => `Girone ${m[1]}: con compagni a rotazione servono almeno 4 giocatori — nessuna partita`,
  },
  {
    re: /^Gruppe (.+): (.+) und (.+) spielen nie zusammen \(bei (\d+) Spielern geht es nicht auf — glatt mit 4, 5, 8 oder 9\)$/,
    en: (m) => `Group ${m[1]}: ${m[2]} and ${m[3]} never play together (with ${m[4]} players it does not work out — it does with 4, 5, 8 or 9)`,
    it: (m) => `Girone ${m[1]}: ${m[2]} e ${m[3]} non giocano mai insieme (con ${m[4]} giocatori non torna — torna con 4, 5, 8 o 9)`,
  },
  {
    re: /^In Gruppe (.+) wird schon gespielt — mit wechselnden Partnern geht Nachmelden nur vorher$/,
    en: (m) => `Group ${m[1]} has already started — with rotating partners, late entries are only possible before`,
    it: (m) => `Nel girone ${m[1]} si gioca già — con compagni a rotazione l’iscrizione tardiva è possibile solo prima`,
  },
  {
    re: /^(.+): alle Gruppen spielen schon — mit wechselnden Partnern kein Nachmelden mehr möglich$/,
    en: (m) => `${m[1]}: all groups have started — with rotating partners late entry is no longer possible`,
    it: (m) => `${m[1]}: tutti i gironi sono già iniziati — con compagni a rotazione non si può più aggiungere`,
  },
  {
    re: /^Nachgemeldet in Gruppe (.+): Spielplan der Gruppe neu erstellt$/,
    en: (m) => `Added late to group ${m[1]}: the group schedule was rebuilt`,
    it: (m) => `Iscritto in ritardo nel girone ${m[1]}: calendario del girone rifatto`,
  },
  { re: /^Vorphase (\d+) ist nicht abgeschlossen$/, en: (m) => `Previous phase ${m[1]} is not finished`, it: (m) => `La fase precedente ${m[1]} non è conclusa` },
  {
    re: /^Gruppen ungleich groß \((.+)\) — gruppenübergreifender Vergleich wird pro Spiel gewertet$/,
    en: (m) => `Groups of unequal size (${tm(m[1] ?? '')}) — the cross-group comparison is scored per match`,
    it: (m) => `Gironi di dimensioni diverse (${tm(m[1] ?? '')}) — il confronto fra gironi è valutato per partita`,
  },
  {
    re: /^(\d+) Freilose? an die bestgesetzten Teams$/,
    en: (m) => `${m[1]} bye${m[1] === '1' ? '' : 's'} for the top-seeded teams`,
    it: (m) => `${m[1]} bye alle squadre con la testa di serie migliore`,
  },
  { re: /^(\d+) offene Spiele abgesagt$/, en: (m) => `${m[1]} open matches cancelled`, it: (m) => `${m[1]} partite aperte annullate` },
  // Engine — Ergebnisprüfung (rules/validate.ts), optional mit „Satz n: "
  { re: set('Punkte müssen ganze Zahlen ≥ 0 sein'), en: (m) => `${s(m)}Scores must be whole numbers ≥ 0`, it: (m) => `${s(m)}I punti devono essere numeri interi ≥ 0` },
  {
    re: set('Innings ergeben (\\d+):(\\d+), eingetragen ist (\\d+):(\\d+)'),
    en: (m) => `${s(m)}Innings add up to ${m[2]}:${m[3]}, entered is ${m[4]}:${m[5]}`,
    it: (m) => `${s(m)}Gli inning danno ${m[2]}:${m[3]}, inserito è ${m[4]}:${m[5]}`,
  },
  { re: set('(\\d+) Innings, erlaubt sind (\\d+)'), en: (m) => `${s(m)}${m[2]} innings, but only ${m[3]} are allowed`, it: (m) => `${s(m)}${m[2]} inning, ma ne sono ammessi solo ${m[3]}` },
  {
    re: set('Abbruch am Limit, aber es ist kein Limit konfiguriert'),
    en: (m) => `${s(m)}Stopped at the limit, but no limit is configured`,
    it: (m) => `${s(m)}Interrotta al limite, ma non è configurato alcun limite`,
  },
  { re: set('Unentschieden mit ungleichem Stand'), en: (m) => `${s(m)}Draw with an unequal score`, it: (m) => `${s(m)}Pareggio con punteggio diverso` },
  { re: set('Unentschieden ist in dieser Phase nicht erlaubt'), en: (m) => `${s(m)}Draws are not allowed in this phase`, it: (m) => `${s(m)}I pareggi non sono ammessi in questa fase` },
  { re: set('Punktziel (\\d+) nicht erreicht'), en: (m) => `${s(m)}Target score ${m[2]} not reached`, it: (m) => `${s(m)}Punteggio obiettivo ${m[2]} non raggiunto` },
  {
    re: set('Mit Bust-Regel kann niemand über (\\d+) kommen'),
    en: (m) => `${s(m)}With the bust rule nobody can go above ${m[2]}`,
    it: (m) => `${s(m)}Con la regola bust nessuno può superare ${m[2]}`,
  },
  { re: set('Beide Teams über dem Punktziel'), en: (m) => `${s(m)}Both teams above the target score`, it: (m) => `${s(m)}Entrambe le squadre sopra il punteggio obiettivo` },
  { re: set('Deckel erreicht, aber kein Vorsprung'), en: (m) => `${s(m)}Cap reached, but no lead`, it: (m) => `${s(m)}Tetto raggiunto, ma nessun vantaggio` },
  { re: set('Sieg nur mit 2 Punkten Vorsprung'), en: (m) => `${s(m)}A win needs a lead of 2 points`, it: (m) => `${s(m)}La vittoria richiede 2 punti di vantaggio` },
  { re: set('Über dem Deckel (\\d+)'), en: (m) => `${s(m)}Above the cap ${m[2]}`, it: (m) => `${s(m)}Sopra il tetto ${m[2]}` },
  {
    re: /^Eingetragener Sieger passt nicht zu den Sätzen \(rechnerisch (.+)\)$/,
    en: (m) => `Entered winner does not match the sets (calculated ${m[1]})`,
    it: (m) => `Il vincitore inserito non corrisponde ai set (calcolato ${m[1]})`,
  },
  { re: /^Best-of-(\d+): kein Team hat (\d+) Sätze gewonnen$/, en: (m) => `Best-of-${m[1]}: no team has won ${m[2]} sets`, it: (m) => `Al meglio di ${m[1]}: nessuna squadra ha vinto ${m[2]} set` },
  { re: /^Mehr als (\d+) Sätze$/, en: (m) => `More than ${m[1]} sets`, it: (m) => `Più di ${m[1]} set` },
  { re: /^Es müssen genau (\d+) Sätze gespielt werden$/, en: (m) => `Exactly ${m[1]} sets must be played`, it: (m) => `Vanno giocati esattamente ${m[1]} set` },
  // Engine — Warteschlange (schedule/queue.ts)
  { re: /^Pause bis (\d+) min$/, en: (m) => `Break, ${m[1]} min left`, it: (m) => `Pausa, ancora ${m[1]} min` },
  // Engine — Tabelle (standings/standings.ts, tiebreakNotes)
  {
    re: new RegExp(`^direkter Vergleich: (${CRITS}) ([+-]?\\d+(?:,\\d+)?) untereinander$`),
    en: (m) => `head-to-head: ${crit(m[1], 0)} ${m[2]} among themselves`,
    it: (m) => `scontro diretto: ${crit(m[1], 1)} ${m[2]} fra loro`,
  },
  {
    re: new RegExp(`^(${CRITS}) ([+-]?\\d+(?:,\\d+)?)( \\(pro Spiel gewertet\\))?$`),
    en: (m) => `${crit(m[1], 0)} ${m[2]}${m[3] ? ' (scored per match)' : ''}`,
    it: (m) => `${crit(m[1], 1)} ${m[2]}${m[3] ? ' (valutato per partita)' : ''}`,
  },
  // Engine — Gruppen-Layout (seeding/groups.ts)
  { re: /^(\d+) Gruppen?: (.+)$/, en: (m) => `${m[1]} group${m[1] === '1' ? '' : 's'}: ${sizes(m[2], 'teams')}`, it: (m) => `${m[1]} giron${m[1] === '1' ? 'e' : 'i'}: ${sizes(m[2], 'squadre')}` },
  // Import (packages/io: mapping.ts, clubs.ts, backup.ts)
  { re: /^(\d+) von (\d+) Spielern — Partner offen$/, en: (m) => `${m[1]} of ${m[2]} players — partner missing`, it: (m) => `${m[1]} di ${m[2]} giocatori — manca il compagno` },
  { re: /^Teamname „(.+)" existiert schon im Turnier$/, en: (m) => `Team name “${m[1]}” already exists in the tournament`, it: (m) => `Il nome squadra «${m[1]}» esiste già nel torneo` },
  { re: /^Teamname „(.+)" doppelt \(auch Zeile (\d+)\)$/, en: (m) => `Team name “${m[1]}” duplicated (also row ${m[2]})`, it: (m) => `Nome squadra «${m[1]}» doppio (anche riga ${m[2]})` },
  { re: /^(\d+) Spieler, Bewerb erwartet (\d+)$/, en: (m) => `${m[1]} players, the competition expects ${m[2]}`, it: (m) => `${m[1]} giocatori, la competizione ne prevede ${m[2]}` },
  { re: /^Setzung „(.+)" ist keine Zahl$/, en: (m) => `Seed “${m[1]}” is not a number`, it: (m) => `La testa di serie «${m[1]}» non è un numero` },
  { re: /^„(.+)" bei Bezahlt nicht erkannt$/, en: (m) => `“${m[1]}” not recognised under Paid`, it: (m) => `«${m[1]}» non riconosciuto alla voce Pagato` },
  { re: /^E-Mail „(.+)" ungültig — leer importiert$/, en: (m) => `E-mail “${m[1]}” invalid — imported empty`, it: (m) => `E-mail «${m[1]}» non valida — importata vuota` },
  { re: /^Zeile (\d+): Mannschaft fehlt$/, en: (m) => `Row ${m[1]}: club missing`, it: (m) => `Riga ${m[1]}: club mancante` },
  {
    re: /^(\d+) Paare ohne Mannschaft — Spalte „Mannschaft" ergänzen oder später auf der Teams-Seite setzen$/,
    en: (m) => `${m[1]} pairs without club — add a “Club” column or set it later on the Teams page`,
    it: (m) => `${m[1]} coppie senza club — aggiungi una colonna “Club” o impostalo dopo nella pagina Squadre`,
  },
  {
    re: /^(.+): (\d+) Spieler — Paar (\d+) ist unvollständig$/,
    en: (m) => `${m[1]}: ${m[2]} players — pair ${m[3]} is incomplete`,
    it: (m) => `${m[1]}: ${m[2]} giocatori — la coppia ${m[3]} è incompleta`,
  },
  { re: /^(.+) steht auch in Zeile (\d+)$/, en: (m) => `${m[1]} also appears in row ${m[2]}`, it: (m) => `${m[1]} compare anche nella riga ${m[2]}` },
  { re: /^Unbekannte Backup-Version (.+)$/, en: (m) => `Unknown backup version ${m[1]}`, it: (m) => `Versione di backup ${m[1]} sconosciuta` },
  // App-Speicher und Sync-Server (store/TournamentProvider.tsx, store/sync.ts, services/sync)
  { re: /^Speichern fehlgeschlagen: (.+)$/, en: (m) => `Saving failed: ${tm(m[1] ?? '')}`, it: (m) => `Salvataggio non riuscito: ${tm(m[1] ?? '')}` },
  {
    re: /^(\d+) eigene Aktion\(en\) kollidierten mit einem anderen Gerät und wurden verworfen$/,
    en: (m) => `${m[1]} of your own action(s) collided with another device and were discarded`,
    it: (m) => `${m[1]} azioni di questo dispositivo sono entrate in conflitto con un altro e sono state scartate`,
  },
  { re: /^Server antwortet nicht mit JSON \(HTTP (\d+)\)$/, en: (m) => `Server does not answer with JSON (HTTP ${m[1]})`, it: (m) => `Il server non risponde in JSON (HTTP ${m[1]})` },
  { re: /^Maximal (\d+) Mitorganisatoren$/, en: (m) => `At most ${m[1]} co-organisers`, it: (m) => `Al massimo ${m[1]} co-organizzatori` },
  { re: /^Maximal (\d+) Aktionen je Anfrage$/, en: (m) => `At most ${m[1]} actions per request`, it: (m) => `Al massimo ${m[1]} azioni per richiesta` },
  { re: /^(.+) ist schon angemeldet \((.+)\)$/, en: (m) => `${m[1]} is already registered (${m[2]})`, it: (m) => `${m[1]} è già iscritto (${m[2]})` },
];

/** Meldung aus Engine/io/Server übersetzen; Deutsch bleibt unverändert */
export function tm(msg: string): string {
  const lang: Lang = getLang();
  if (lang === 'de' || !msg) return msg;
  const exact = engineEintrag(msg);
  if (exact) return exact[lang];
  for (const p of PATTERNS) {
    const m = p.re.exec(msg);
    if (m) return lang === 'en' ? p.en(m) : p.it(m);
  }
  // Manche dieser Texte stehen schon im übrigen Wörterbuch (z. B. Beitreten-Seite)
  return woerterbuchEintrag(msg)?.[lang] ?? msg;
}

/** Für Komponenten: rendert neu, wenn die Sprache wechselt */
export function useTm(): typeof tm {
  useLang();
  return tm;
}
